import { env } from 'config/env';

import type { LocationId, RmReportId } from '@brightlocal/shared';
import { isBelowBreakpoint } from '@brightlocal/ui-components/hooks/use-breakpoint';

import { captureSentryException, LocalStorage } from 'shared/utils';

import { OLD_PLATFORM_REDIRECT_DISMISSED_KEY, oldPlatformRedirectDismissedCodec } from '../consts';
import { useOldPlatformRedirectModalStore } from '../hooks/useOldPlatformRedirectModalStore';

/** Old Platform path for the plan chooser — CTA destination for users without AI Insights access. */
export const OLD_PLATFORM_UPGRADE_PATH = '/seo-tools/admin/plans/choose';

/**
 * URL `action` param value appended by New Platform when redirecting a mobile
 * user to Old Platform from flows that should show the mobile-warning modal,
 * including sign-up and post-onboarding/insights redirects.
 * Old Platform reads this param and shows the mobile-warning modal.
 */
export const ACTION_NP_MOBILE_WARNING = 'np-mobile-warning';

/**
 * Old Platform location-dashboard sub-section segments accepted by {@link buildLocationDashboardPath}.
 */
export const LOCATION_DASHBOARD_SEGMENT = {
  EDIT: 'edit',
  SETTINGS: 'settings',
  AI_INSIGHTS: 'insights',
  RANKINGS: 'lsrc',
  /** Local Rank Tracker report settings. */
  RANKINGS_EDIT: 'lsrc/edit',
  CITATIONS: 'cb',
  CT: 'ct',
  GBP: 'gpw',
  GBP_POSTS: 'gbp-posts/view',
  REPUTATION_REVIEWS: 'rm',
  REPUTATION_EDIT: 'rm/edit',
  /** Review Builder: the old platform's Get Reviews page. */
  REPUTATION_GET_REVIEWS: 'rm/get-reviews',
  LSG_ADD: 'lsg/add',
  LSG_EDIT: 'lsg/edit'
} as const;

/** Union of all Old Platform location-dashboard segment identifiers. */
export type LocationDashboardSegment = (typeof LOCATION_DASHBOARD_SEGMENT)[keyof typeof LOCATION_DASHBOARD_SEGMENT];

/** Old Platform path to the all-locations list. */
export const ALL_LOCATIONS_PATH = '/seo-tools/admin/all-locations';

/**
 * Old Platform manual add-location form path — the "continue without a Google Business Profile"
 * fallback destination for basic (non-CB) onboarding packages.
 */
export const ADD_LOCATION_PATH = '/seo-tools/admin/add-location?showLocationDetailsForm=true';

/**
 * Old Platform CB campaign-creation path — the "continue without a Google Business Profile"
 * fallback destination for Simply Listings (CB-only) onboarding packages.
 */
export const CB_CAMPAIGN_CREATE_PATH = '/seo-tools/admin/cb/campaigns/create?showLocationDetailsForm=true';

/**
 * Returns the old platform URL for the given path.
 * @param path - The path to redirect to.
 * @returns The old platform URL.
 */
export const getOldPlatformUrl = (path = '/seo-tools/admin/login') => {
  if (!env.oldPlatformBaseUrl) {
    captureSentryException({
      message: 'Old platform base URL is not set.',
      feature: 'shared',
      extra: { path }
    });
    // Fallback to the production website if the old platform base URL is not set.
    return `https://tools.brightlocal.com${path}`;
  }

  return `${env.oldPlatformBaseUrl}${path}`;
};

/** Whether the user has dismissed the Old Platform redirect confirmation modal for good. */
const isOldPlatformRedirectDismissed = () =>
  LocalStorage.get(OLD_PLATFORM_REDIRECT_DISMISSED_KEY, oldPlatformRedirectDismissedCodec) === true;

/**
 * Opens an Old Platform URL in a new tab. Shows a one-time confirmation modal
 * first, unless the user has previously dismissed it for good.
 */
export const redirectToOldPlatform = (path?: string) => {
  const url = getOldPlatformUrl(path);
  const isModalDismissed = isOldPlatformRedirectDismissed();

  if (isModalDismissed) {
    window.open(url, '_blank', 'noreferrer');
    return;
  }

  useOldPlatformRedirectModalStore.getState().open(url);
};

/**
 * Builds an Old Platform location-dashboard path for a given location and segment.
 * @param locationId - The numeric or string location ID.
 * @param segment - The dashboard sub-section, one of {@link LOCATION_DASHBOARD_SEGMENT}.
 */
export const buildLocationDashboardPath = (locationId: number | string, segment: LocationDashboardSegment) =>
  `/seo-tools/admin/location-dashboard/location/${locationId}/${segment}`;

/** Payment method the Citation Builder checkout hands to the Old Platform Finalize page via `?paymentMethod=`. */
export const PAYMENT_METHOD = {
  CARD: 'card',
  CREDITS: 'credits'
} as const;

/** One of the Old Platform checkout payment methods. */
export type PaymentMethod = (typeof PAYMENT_METHOD)[keyof typeof PAYMENT_METHOD];

/**
 * Old Platform Citation Builder checkout path — reads `?paymentMethod=` to
 * pre-select Card vs Credits; the customer still confirms payment there. Keeps
 * all Old Platform redirect targets in one place.
 *
 * Points at `/pay`, a page built for orders reviewed here, rather than the older
 * `/finalize`. That one belongs to the Old Platform bundles experiment and
 * re-renders its own order summary and price estimate, which a customer who has
 * already reviewed and confirmed in the wizard should not be shown again.
 * Backing out of payment on `/pay` returns to the Review & pay step.
 *
 * @param locationId - The location the campaign belongs to.
 * @param paymentMethod - The pre-selected payment method.
 */
export const buildCheckoutUrl = (locationId: number | string, paymentMethod: PaymentMethod) =>
  `${buildLocationDashboardPath(locationId, LOCATION_DASHBOARD_SEGMENT.CITATIONS)}/pay?paymentMethod=${paymentMethod}`;

/** Old Platform Citation Builder overview path — hosts the account-level buy-credits dialog. */
const CB_OVERVIEW_PATH = '/seo-tools/admin/cb';

/**
 * Old Platform CB overview deep link that opens the buy-credits dialog on load.
 * `location_id` tells Old Platform which campaign the customer left, so after a
 * purchase (or a cancelled one) it can send them back to that campaign's Review &
 * pay step through its hand-off interstitial.
 *
 * @param locationId - The location whose campaign the customer is paying for.
 */
export const buildBuyCbCreditsUrl = (locationId: LocationId) =>
  `${CB_OVERVIEW_PATH}?buy-credits=1&location_id=${locationId}`;

/**
 * Builds an Old Platform report-creation-wizard path for a given location, scrolled to the
 * reports-and-connections section.
 * @param locationId - The numeric or string location ID.
 */
export const buildReportCreationWizardPath = (locationId: number | string) =>
  `/seo-tools/admin/report-creation-wizard/${locationId}#reports_and_connections`;

/**
 * Builds an Old Platform LSG report run-history-list path for a given location and report.
 * @param locationId - The numeric or string location ID.
 * @param lsgReportId - The numeric or string LSG report ID.
 */
export const buildLsgReportRunsListPath = (locationId: number | string, lsgReportId: number | string) =>
  `/seo-tools/admin/locations/${locationId}/lsg/reports/${lsgReportId}/runs/list`;

/**
 * Builds the Old Platform settings page path of a Reputation Manager report.
 * @param params.locationId - The location the report belongs to.
 * @param params.reportId - The Reputation Manager report.
 */
export const buildRmReportSettingsPath = ({ locationId, reportId }: { locationId: LocationId; reportId: RmReportId }) =>
  `${buildLocationDashboardPath(locationId, LOCATION_DASHBOARD_SEGMENT.REPUTATION_EDIT)}?reportId=${reportId}`;

/**
 * Hard-replaces the current URL with an Old Platform URL, handing the user off
 * without tearing down the New Platform session — the httpOnly refresh cookie
 * survives, so returning to the New Platform silently re-authenticates the user
 * (accepted NP-114 trade-off). Appends the `np-mobile-warning` action param on
 * mobile so Old Platform can show the mobile-warning modal — joined with `&`
 * when the path already carries a query string.
 *
 * The skip-logout hand-off began as a pre-launch, feature-flag-gated migration
 * mechanism. Its only remaining caller is the Simply Listings / CB completion
 * bounce, which is temporary and goes away once Citation Builder lives on the
 * New Platform.
 *
 * Returns a `Promise<never>` that intentionally never resolves. Once
 * `window.location.replace` triggers the browser navigation, the browser owns
 * the rest. Hanging here keeps the caller's pending UI (route `pendingComponent`,
 * spinner, etc.) on screen until the browser navigates away.
 */
export const bounceToOldPlatform = (path: string) => {
  const querySeparator = path.includes('?') ? '&' : '?';
  const mobileQueryParam = isBelowBreakpoint() ? `${querySeparator}action=${ACTION_NP_MOBILE_WARNING}` : '';
  const url = getOldPlatformUrl(`${path}${mobileQueryParam}`);

  window.location.replace(url);

  // eslint-disable-next-line @typescript-eslint/no-empty-function -- intentional hang; see doc comment above.
  return new Promise<never>(() => {});
};
